import { useAuthenticatedAxiosInstance } from './useAuthenticatedAxiosInstance';
import { useCallback } from 'react';
import { getEnvironmentConfig } from '../environmentConfig';

export interface SecretMetadata {
    id: string;
    name: string;
    description: string | null;
    service: string | null;
    scope: 'ORG' | 'USER' | 'GRAPH';
    scopeId: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateSecretInput {
    name: string;
    value: string;
    description?: string;
    service?: string;
}

export interface UpdateSecretInput {
    value?: string;
    description?: string;
    service?: string;
}

export const useSecretsApi = () => {
    const api = useAuthenticatedAxiosInstance(`${getEnvironmentConfig().graphServiceUrl}`);

    const listUserSecrets = useCallback(async (): Promise<SecretMetadata[]> => {
        const result = await api.get('/secrets/user');
        return result.data.secrets;
    }, [api]);

    const createUserSecret = useCallback(
        async (data: CreateSecretInput): Promise<SecretMetadata> => {
            const result = await api.post('/secrets/user', data);
            return result.data.secret;
        },
        [api],
    );

    const updateUserSecret = useCallback(
        async (id: string, data: UpdateSecretInput): Promise<SecretMetadata> => {
            const result = await api.put(`/secrets/user/${id}`, data);
            return result.data.secret;
        },
        [api],
    );

    const deleteUserSecret = useCallback(
        async (id: string): Promise<void> => {
            await api.delete(`/secrets/user/${id}`);
        },
        [api],
    );

    const listOrgSecrets = useCallback(async (): Promise<SecretMetadata[]> => {
        const result = await api.get('/secrets/org');
        return result.data.secrets;
    }, [api]);

    const createOrgSecret = useCallback(
        async (data: CreateSecretInput): Promise<SecretMetadata> => {
            const result = await api.post('/secrets/org', data);
            return result.data.secret;
        },
        [api],
    );

    const updateOrgSecret = useCallback(
        async (id: string, data: UpdateSecretInput): Promise<SecretMetadata> => {
            const result = await api.put(`/secrets/org/${id}`, data);
            return result.data.secret;
        },
        [api],
    );

    const deleteOrgSecret = useCallback(
        async (id: string): Promise<void> => {
            await api.delete(`/secrets/org/${id}`);
        },
        [api],
    );

    const listGraphSecrets = useCallback(
        async (graphId: string): Promise<SecretMetadata[]> => {
            const result = await api.get(`/secrets/graph/${graphId}`);
            return result.data.secrets;
        },
        [api],
    );

    const createGraphSecret = useCallback(
        async (graphId: string, data: CreateSecretInput): Promise<SecretMetadata> => {
            const result = await api.post(`/secrets/graph/${graphId}`, data);
            return result.data.secret;
        },
        [api],
    );

    const updateGraphSecret = useCallback(
        async (graphId: string, id: string, data: UpdateSecretInput): Promise<SecretMetadata> => {
            const result = await api.put(`/secrets/graph/${graphId}/${id}`, data);
            return result.data.secret;
        },
        [api],
    );

    const deleteGraphSecret = useCallback(
        async (graphId: string, id: string): Promise<void> => {
            await api.delete(`/secrets/graph/${graphId}/${id}`);
        },
        [api],
    );

    const getAvailableSecrets = useCallback(
        async (graphId: string): Promise<SecretMetadata[]> => {
            const result = await api.get(`/secrets/graph/${graphId}/available`);
            return result.data.secrets;
        },
        [api],
    );

    const generateSecret = useCallback(async (): Promise<string> => {
        const result = await api.post('/secrets/generate');
        return result.data.value;
    }, [api]);

    return {
        listUserSecrets,
        createUserSecret,
        updateUserSecret,
        deleteUserSecret,
        listOrgSecrets,
        createOrgSecret,
        updateOrgSecret,
        deleteOrgSecret,
        listGraphSecrets,
        createGraphSecret,
        updateGraphSecret,
        deleteGraphSecret,
        getAvailableSecrets,
        generateSecret,
    };
};
